import { type MouseEvent, type ReactNode } from 'react'
import type { IndexDef, LevelCode, ObjectiveEvent, ObjectiveState } from '@/api/types/readiness'
import { levelFromNum, levelNum } from '@/api/types/readiness'
import { allObjectives, domainColor, levelName } from '@/lib/indexes/registry'
import { fmtMonth, type Projection, type ObjectiveProjection } from '@/lib/readiness/projection'
import { needsConfirmation } from '@/lib/readiness/score'
import { stepSparklineGeometry } from '@/lib/readiness/miniChart'
import { fmtMonthYear } from '@/lib/readiness/compare'
import { InfoTooltip, TipBody, Tooltip } from '@/components/readiness/Term'
import { useInfoTooltip } from '@/components/readiness/useInfoTooltip'
import { StepSparkline, type SparkHoverTarget } from '@/components/readiness/StepSparkline'
import { ObjectiveName } from '@/components/readiness/ObjectiveName'
import { OriginLink, useOriginNavigate } from '@/lib/readiness/nav'
import type { GateDetail } from '@/lib/readiness/module'
import { formatDate } from '@/components/common/format'

// ── Progress page parts (Iteration 3 §3, §4; Iteration 5 §10, §11; Iteration 6 §5–§9) ──

export const COVERAGE_HELP = 'More assets were found, so the level now describes a larger estate. This is better visibility, not regression.'

// `inline` renders a span trigger for use inside a button or link.
export function CoverageChip({ inline }: { inline?: boolean } = {}) {
  return (
    <Tooltip text={COVERAGE_HELP} className="!no-underline" as={inline ? 'span' : 'button'}>
      <span className="rd-chip" style={{ background: 'var(--rd-coverage-bg)', color: 'var(--rd-coverage)', fontWeight: 600 }} data-testid="coverage-chip">coverage-driven</span>
    </Tooltip>
  )
}

export function KpiTile({ label, value, sub, valueColor, testId }: { label: ReactNode; value: ReactNode; sub?: ReactNode; valueColor?: string; testId?: string }) {
  return (
    <div className="rd-card min-h-[120px] p-5" data-testid={testId}>
      <p className="rd-label">{label}</p>
      <p className="rd-kpi mt-2" style={{ color: valueColor }}>{value}</p>
      {sub && <div className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{sub}</div>}
    </div>
  )
}

// ── Objectives · history and estimate (I6 §5, §6) ───────────────────────────
// Level by objective (I5 §10) and Time-to-level estimate (I5 §11) merged into
// one table: each row reads left to right as one objective's story, history →
// now → projection. The sparkline's hover (§8) and the History cell's keyboard
// tooltip carry the text the retired cards held; the header carries the
// shared scale. Tooltips are informational only (I5 §17.1).

const DAY = 86_400_000
const SPARK_W = 200
const SPARK_H = 48
const PRINT_SPARK_W = 190
const PRINT_SPARK_H = 40

export const CONFIDENCE_LADDER = (org: string): Array<[string, string]> => [
  ['High', `This objective’s own past moves, or 3+ similar moves at ${org}.`],
  ['Medium', `1–2 similar moves, or ${org}’s moves in general.`],
  ['Low', 'Little history, so it uses the QRI’s default timings; or the objective is already overdue. Treat as a rough guide.'],
]

// Confidence header tooltip laid out by level (§9).
export function ConfidenceTip({ org }: { org: string }) {
  return (
    <TipBody title="Confidence">
      <p>How much history the estimate rests on.</p>
      <div className="grid" style={{ gridTemplateColumns: 'max-content 1fr', columnGap: 12, rowGap: 6 }} data-testid="confidence-ladder">
        {CONFIDENCE_LADDER(org).map(([k, v]) => (
          <div key={k} className="contents"><b>{k}</b><span>{v}</span></div>
        ))}
      </div>
    </TipBody>
  )
}

export const HEADER_TIPS = (org: string): Record<string, string> => ({
  'Now → next': 'The objective’s current confirmed level and the next level it’s working towards. “Sustain” means it’s at L3 and the work now is keeping it there.',
  'Time at level': 'Days since this objective reached its current level. Marked “Overdue vs pace” when this is longer than the average time per level.',
  'Avg time per level': `The average number of days a level move takes, used to estimate this objective’s next move. It’s based on this objective’s own past moves where there are any, otherwise ${org}’s moves across objectives, otherwise the QRI default. Recent moves count more.`,
  'Next level by': 'The estimated month for the next level: average time per level minus time already spent. Overdue objectives are set to about a month from now. Vendor-gated objectives can’t be earlier than the vendor’s roadmap date.',
  'L3 by': `The estimated month this objective reaches L3, adding the average time for each remaining move. The latest date across all objectives sets ${org}’s end date.`,
})

const TITLE_TIP = (org: string) => (
  <TipBody title="Objectives · history and estimate">
    <p>How each objective got here, where it is now, and when it is likely to reach its next level and L3 if it keeps moving at {org}’s past pace.</p>
    <p>For each objective, Quantizant takes the average time a level move has taken and subtracts the time already spent at the current level. Average times come from confirmed level changes in the event log, and recent moves count more. For vendor-gated objectives, the vendor’s PQC roadmap date sets the earliest possible date.</p>
    <p>The latest L3 date becomes {org}’s “all objectives at L3” estimate. This is an estimate, not a commitment; it updates with every level change and every daily sync.</p>
  </TipBody>
)

const movesLine = (moves: ObjectiveEvent[]) => (moves.length ? moves.slice(-3).map((e) => `${e.cause === 'coverage' ? '◆ ' : ''}${e.from_level ?? 'L0'}→${e.to_level} ${fmtMonthYear(e.occurred_at)}`).join(' · ') : 'No moves since tracking started')

export function ObjectivesHistoryEstimate({ index, events, theme, q, projection, states, orgName, gateDetails = {}, print = false, onSparkHover }: { index: IndexDef; events: ObjectiveEvent[]; theme: 'light' | 'dark'; q: string; projection: Projection; states: Record<string, ObjectiveState>; orgName: string; gateDetails?: Record<string, GateDetail>; print?: boolean; onSparkHover?: (target: SparkHoverTarget) => void }) {
  const org = orgName || 'the organisation'
  const sorted = [...events].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))
  // Tracking start (I5 §10.2) = the first readiness event; the same date is the
  // sparklines' left edge and the start of `Time at level` for objectives that
  // never moved (projection.ts walks from the same first event).
  const start = sorted[0]?.occurred_at ?? new Date().toISOString()
  const t0 = new Date(start).getTime()
  const t1 = Date.now()
  const byCode = new Map(projection.objectives.map((o) => [o.code, o]))
  const deadline = index.submission?.deadline ?? null
  const tips = HEADER_TIPS(org)
  // Print has no hover, so headers render as plain text there; the terms are
  // explained in How to read this report (§11.4).
  const head = (h: string, tip?: ReactNode) => (print || !tip ? <span className="font-medium">{h}</span> : <InfoTooltip width={h === 'Confidence' ? 340 : 320} testId={h === 'Confidence' ? 'confidence-tooltip' : undefined} content={tip}><span className="font-medium">{h}</span></InfoTooltip>)
  const th = (h: string) => (
    <th key={h} className="px-2 py-2 text-left align-bottom font-medium">{head(h, tips[h] ? <TipBody title={h}><p>{tips[h]}</p></TipBody> : undefined)}</th>
  )
  const sw = print ? PRINT_SPARK_W : SPARK_W
  const sh = print ? PRINT_SPARK_H : SPARK_H
  const projStyle = { background: 'var(--rd-projection-tint)' }
  const histStyle = { background: 'var(--rd-history-tint)' }

  return (
    <section className="rd-card p-5" aria-labelledby="rd-ohe" data-testid="objectives-history-estimate">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {print ? <h2 id="rd-ohe" className="rd-section-title">Objectives · history and estimate</h2> : (
          <InfoTooltip width={380} testId="ohe-title-tooltip" content={TITLE_TIP(org)}>
            <h2 id="rd-ohe" className="rd-section-title">Objectives · history and estimate</h2>
          </InfoTooltip>
        )}
      </div>
      <p className="mt-1 max-w-4xl text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }} data-testid="ohe-subtitle">
        Each row reads left to right: how the objective got here since tracking started ({formatDate(start)}), where it is now, and when it is likely to move next.
      </p>
      <p className="rd-mono mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }} aria-label="Legend">
        <span>— Confirmed level</span>
        <span>┄ Awaiting confirmation</span>
        <span><span style={{ color: 'var(--rd-coverage)' }}>◆</span> Coverage-driven drop</span>
        <span>● Today</span>
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-[13.5px]" style={{ minWidth: print ? 0 : 900 }}>
          <thead>
            <tr className="rd-col-group" data-testid="ohe-groups">
              <th />
              <th className="px-2 pt-1 text-left" style={{ ...histStyle, color: 'var(--rd-coverage)' }}>HISTORY</th>
              <th colSpan={2} className="px-2 pt-1 text-left" style={{ color: 'var(--rd-text-muted)' }}>NOW</th>
              <th colSpan={4} className="px-2 pt-1 text-left" style={{ ...projStyle, color: 'var(--rd-projection)' }}>PROJECTION</th>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--rd-border-subtle)', color: 'var(--rd-text-muted)' }}>
              <th className="py-2 pr-2 text-left align-bottom font-medium">Objective</th>
              <th className="px-2 py-2 text-left align-bottom font-medium" style={histStyle}>
                <span className="block">Confirmed level, L0–L3</span>
                <span className="rd-mono block text-[10.5px] font-normal" style={{ color: 'var(--rd-text-faint)' }}>{fmtMonthYear(start)} … Today</span>
              </th>
              {th('Now → next')}
              {th('Time at level')}
              {(['Avg time per level', 'Next level by', 'L3 by'] as const).map((h) => <th key={h} className="px-2 py-2 text-left align-bottom font-medium" style={projStyle}>{head(h, <TipBody title={h}><p>{tips[h]}</p></TipBody>)}</th>)}
              <th className="px-2 py-2 text-left align-bottom font-medium" style={projStyle}>{head('Confidence', <ConfidenceTip org={org} />)}</th>
            </tr>
          </thead>
          <tbody>
            {allObjectives(index).map(({ domain, objective }) => {
              const o = byCode.get(objective.code)
              if (!o) return null
              return <Row key={objective.code} index={index} code={objective.code} title={objective.title} color={domainColor(domain, theme)} events={sorted} t0={t0} t1={t1} q={q} o={o} state={states[objective.code]} driver={projection.summary.driver_code === o.code} gate={gateDetails[o.code]} org={org} print={print} sw={sw} sh={sh} onSparkHover={onSparkHover} />
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }} data-testid="ttl-footer">
        {projection.summary.enough_history ? <>All objectives at L3 ≈ <b style={{ color: 'var(--rd-projection)' }}>{fmtMonth(projection.summary.all_l3_by)}</b></> : 'Not enough history for an end date yet (fewer than two completed transitions): rows run on QRI defaults with Low confidence'} · the latest objective sets the date{deadline ? ` · CSA due ${formatDate(deadline)}` : ''}
      </p>
    </section>
  )
}

function Row({ index, code, title, color, events, t0, t1, q, o, state, driver, gate, org, print, sw, sh, onSparkHover }: { index: IndexDef; code: string; title: string; color: string; events: ObjectiveEvent[]; t0: number; t1: number; q: string; o: ObjectiveProjection; state: ObjectiveState | undefined; driver: boolean; gate: GateDetail | undefined; org: string; print: boolean; sw: number; sh: number; onSparkHover?: (target: SparkHoverTarget) => void }) {
  const navigate = useOriginNavigate()
  const g = stepSparklineGeometry(events, code, t0, t1, sw, sh)
  const level = g.level
  const atL3 = level === 'L3'
  const next = atL3 ? null : levelFromNum(levelNum(level) + 1)
  const suggested = state && needsConfirmation(state) && state.suggested_level && state.suggested_level !== level ? state.suggested_level : null
  const lastMove = g.moves[g.moves.length - 1]
  const days = o.time_at_level ?? Math.round((t1 - (lastMove ? new Date(lastMove.occurred_at).getTime() : t0)) / DAY)
  const drops = g.drops.length
  const reachedL3 = atL3 ? new Date(t1 - days * DAY).toISOString() : null
  const to = `/readiness/${code}${q}`
  const moves3 = g.moves.slice(-3).map((e) => `${e.from_level ?? 'L0'}→${e.to_level} ${fmtMonthYear(e.occurred_at)}`)
  // Card tooltip (I5 §10.2), attached to the History cell on keyboard focus (§5.2).
  const cardTip = (
    <TipBody title={`${code} · ${title}`}>
      <p><span className="inline-block w-14" style={{ color: 'var(--rd-text-muted)' }}>Now</span>L{levelNum(level)} {levelName(index, level)} · {days} d at this level{o.overdue && <span style={{ color: 'var(--rd-status-warn)' }}> · overdue vs pace</span>}</p>
      {suggested ? (
        <p><span className="inline-block w-14" style={{ color: 'var(--rd-text-muted)' }}>Pending</span>Sync suggests {suggested}. Confirm it to record the move.</p>
      ) : (
        <p><span className="inline-block w-14" style={{ color: 'var(--rd-text-muted)' }}>Moves</span>{moves3.length ? moves3.join(' · ') : 'none yet'}</p>
      )}
      <p style={{ color: 'var(--rd-projection)' }}><span className="inline-block w-14" style={{ color: 'var(--rd-text-muted)' }}>Next</span>{next ? `${next} est. ${fmtMonth(o.next_by)} · ${o.confidence}` : 'Sustain at L3'} <span className="rd-chip rd-chip-sm" style={{ background: 'var(--rd-projection-bg)', color: 'var(--rd-projection)', letterSpacing: '0.06em' }}>PROJECTION</span></p>
    </TipBody>
  )
  const tip = useInfoTooltip<HTMLTableCellElement>({ content: cardTip, width: 340, testId: `ohe-tooltip-${code}`, disabled: print })
  const { ref, onFocus, onBlur, 'aria-describedby': described } = tip.triggerProps
  const onRowClick = (e: MouseEvent<HTMLTableRowElement>) => {
    if ((e.target as Element).closest('a, button, [role="tooltip"]')) return
    navigate(to, e.currentTarget)
  }
  const flagStyle = { textDecoration: 'underline dotted', textUnderlineOffset: 3 } as const
  const mono = 'rd-mono rd-id'
  const proj = { background: 'var(--rd-projection-tint)' }
  return (
    <tr className={print ? undefined : 'rd-row-link'} style={{ borderBottom: '1px solid var(--rd-border-subtle)', height: print ? undefined : 62, verticalAlign: 'middle' }} onClick={print ? undefined : onRowClick} data-testid={`ohe-${code}`} data-origin-key={to}>
      <td className="py-2 pr-2 align-middle">
        <OriginLink to={to} className="flex items-start gap-2 hover:underline" style={{ color: 'inherit' }} data-testid={`ohe-link-${code}`}>
          <span className={`${mono} w-10 shrink-0 font-semibold`} style={{ color: 'var(--rd-text-muted)' }}>{code}</span>
          <span className="min-w-0">
            <ObjectiveName name={title} />
            {driver && (print ? <span className="block text-[11px]" style={{ color: 'var(--rd-projection)' }}>drives the end date</span> : (
              <InfoTooltip as="span" plain className="mt-0.5 block" width={300} content={<TipBody title="Drives the end date"><p>{code} has the latest L3 date, so it sets {org}’s ‘all objectives at L3’ estimate. Bringing it forward brings the whole date forward.</p></TipBody>}>
                <span className="text-[11px]" style={{ color: 'var(--rd-projection)', ...flagStyle }}>drives the end date</span>
              </InfoTooltip>
            ))}
          </span>
        </OriginLink>
      </td>
      <td ref={ref} className="px-2 py-1.5 align-middle" style={{ background: 'var(--rd-history-tint)', width: sw + 16 }} tabIndex={print ? undefined : 0} onFocus={onFocus} onBlur={onBlur} aria-describedby={described} data-testid={`ohe-history-${code}`}>
        <StepSparkline index={index} events={events} code={code} from={t0} to={t1} color={color} width={sw} height={sh} suggested={suggested} interactive={!print} onHover={onSparkHover} ariaLabel={`${code} confirmed level over time: ${g.moves.map((e) => e.to_level).join(', ') || 'L0'}`} testId={`sparkline-${code}`} />
        {print && <p className="rd-mono rd-print-moves mt-0.5 text-[9px]" style={{ color: 'var(--rd-text-faint)' }} data-testid={`moves-line-${code}`}>{movesLine(g.moves)}</p>}
        {tip.node}
      </td>
      <td className="px-2 py-1.5 align-middle" data-testid={`ohe-next-${code}`}>
        <span className="flex items-center gap-1.5">
          <span className={mono}>{next ? `${level} → ${next}` : 'Sustain'}</span>
          {suggested && <span className="rd-chip rd-chip-sm" style={{ border: '1px dashed var(--rd-border-control)', color: 'var(--rd-text-muted)', background: 'transparent' }} data-testid={`ohe-suggested-${code}`}>{suggested} suggested</span>}
        </span>
        <span className="block text-[11px]" style={{ color: 'var(--rd-text-faint)' }}>{levelName(index, level)}</span>
      </td>
      <td className="px-2 py-1.5 align-middle" data-testid={`ohe-time-${code}`}>
        <span className={`${mono} block`}>{days} d</span>
        <span className="rd-print-nowrap block text-[11.5px]" style={{ minHeight: 16 }}>
          {o.overdue ? (
            print ? <span style={{ color: 'var(--rd-status-warn)' }}>Overdue vs pace</span> : (
              <InfoTooltip as="span" plain width={300} content={<TipBody title="Overdue vs pace"><p>At this level for {o.time_at_level} d; the average time per level is {o.avg_dwell ?? '—'} d. The next-level date is set to about a month from now.</p></TipBody>}>
                <span style={{ color: 'var(--rd-status-warn)', ...flagStyle }}>Overdue vs pace</span>
              </InfoTooltip>
            )
          ) : drops > 0 ? (
            print ? <span style={{ color: 'var(--rd-coverage)' }}>{drops} coverage-driven {drops === 1 ? 'drop' : 'drops'}</span> : (
              <InfoTooltip as="span" plain width={300} content={<>{COVERAGE_HELP}</>}>
                <span style={{ color: 'var(--rd-coverage)', ...flagStyle }}>{drops} coverage-driven {drops === 1 ? 'drop' : 'drops'}</span>
              </InfoTooltip>
            )
          ) : null}
        </span>
      </td>
      <td className={`${mono} px-2 py-1.5 align-middle`} style={proj} data-testid={`ohe-avg-${code}`}>{atL3 ? '—' : o.avg_dwell != null ? `${o.avg_dwell} d` : '—'}</td>
      <td className="px-2 py-1.5 align-middle" style={proj} data-testid={`ohe-nextby-${code}`}>
        <span className={mono} style={{ color: atL3 ? undefined : 'var(--rd-projection)' }}>{atL3 ? '—' : fmtMonth(o.next_by)}</span>
        {!atL3 && o.gated_until && (
          <span className="ml-1.5 inline-flex">
            {print ? <span className="text-[11.5px]" style={{ color: 'var(--rd-status-warn)' }}>vendor-gated</span> : (
              <InfoTooltip as="span" plain width={320} content={<TipBody title="Vendor-gated"><p>Can’t move up before {gate?.vendor ?? 'the vendor'} ships PQC for {gate?.capability ?? 'this capability'} (roadmap {gate?.roadmap_date ? fmtMonth(`${gate.roadmap_date}-01T00:00:00Z`) : fmtMonth(o.gated_until)}, {gate?.confidence ?? '—'}). Click for the catalogue entry.</p></TipBody>}>
                {gate ? (
                  <OriginLink to={`/readiness/catalogue/${gate.technology_id}${q}`} className="text-[11.5px]" style={{ color: 'var(--rd-status-warn)', ...flagStyle }} data-testid={`ttl-gate-${code}`}>vendor-gated</OriginLink>
                ) : (
                  <span className="text-[11.5px]" style={{ color: 'var(--rd-status-warn)' }}>vendor-gated</span>
                )}
              </InfoTooltip>
            )}
          </span>
        )}
      </td>
      <td className={`${mono} px-2 py-1.5 align-middle`} style={{ ...proj, color: atL3 ? 'var(--rd-text-muted)' : 'var(--rd-projection)', fontWeight: driver ? 700 : undefined }} data-testid={`ohe-l3by-${code}`}>{atL3 ? `Reached ${fmtMonthYear(reachedL3)}` : fmtMonth(o.l3_by)}</td>
      <td className="px-2 py-1.5 align-middle" style={proj} data-testid={`ohe-conf-${code}`}>{atL3 ? '—' : o.confidence}</td>
    </tr>
  )
}

export type { LevelCode }
